import { createElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCommandRegistry } from "../../../contexts/CommandRegistryContext";
import { useCommandFreeText } from "../../../contexts/CommandFreeTextContext";
import { MAGICBI_LOGO_SRC } from "../../../constants/brandAssets";
import { gridKeyboardShortcuts } from "../../../hooks/gridKeyboardNavigation.copy";
import { chordMatches } from "../../../services/commands/keyboard";
import { getSpeechProvider } from "../../../services/speech/speechProviders";
import { notify } from "../../../services/notify";
import { getApiErrorMessage } from "../../../services/apiErrors";
import type { NotificationKind } from "../../../services/notify";
import type {
  CommandAction,
  CommandArgSpec,
  CommandArgs,
  CommandContext,
  CommandResolution,
  CommandResult,
  KeyChord,
} from "../../../services/commands/commands.types";
import type { StoryFieldApi } from "../../../services/storyCanvas/storyFilters.types";
import { formatRelativeTime } from "../../../utils/formatRelativeTime";
import { rankByFuzzy } from "../../../utils/fuzzyMatch";
import type { CommandListGroup, CommandListItem } from "../../molecules/CommandList";
import { mergeShortcutPairs } from "../ShortcutsDialog";
import type { ShortcutGroup } from "../ShortcutsDialog";
import { COMMAND_PALETTE_COPY } from "./CommandPaletteHost.copy";
import { commandGroupLabel, glyphFor, listCommandGroups, OPEN_PALETTE_ACTION_ID } from "./commandGroups";
import { EVERYWHERE_SHORTCUTS, SHORTCUT_PAIRS, VOICE_CHORD } from "./staticShortcuts";
import type { PaletteStory } from "./usePaletteSearchSources";
import { usePaletteSearchSources } from "./usePaletteSearchSources";

type EntitySpec = Extract<CommandArgSpec, { kind: "entity" }>;
const PALETTE_CONTEXT: CommandContext = { source: "palette" };
const INTENT_ID = "__intent";
const AMBIGUOUS_ID = "__ambiguous";
/** Groups drawn as a strip of pills when they hold only "Soon" rows. */
const STRIP_GROUP_IDS: readonly string[] = ["comingSoon"];
const FREE_TEXT_ID = "__freeText";
const STORY_PREFIX = "story:";
const FIELD_PREFIX = "field:";
/** Most rows one search group shows. */
const GROUP_LIMIT = 5;
/** The registered action that adds a story filter on a field (arg `field`), when the page offers one. */
const ADD_FILTER_ACTION = "story.addFilter";
/** Row ids of actions and of argument options are told apart by this prefix. */
const ACTION_PREFIX = "action:";
/** The shown chord of the "Ask" row; the palette runs the row on Mod+Enter. */
const ASK_CHORD: readonly KeyChord[] = [{ key: "Enter", mod: true }];

interface ArgStep {
  action: CommandAction;
  /** Arguments chosen so far. */
  args: CommandArgs;
  spec: EntitySpec;
}

const RESULT_KIND: Record<CommandResult["status"], NotificationKind | null> = {
  done: "success",
  blocked: "info",
  failed: "error",
  cancelled: null,
};

const requiredEntitySpecs = (action: CommandAction): EntitySpec[] =>
  (action.args ?? []).filter((spec): spec is EntitySpec => spec.kind === "entity" && Boolean(spec.required));

const availabilityOf = (action: CommandAction) => {
  try {
    return action.isAvailable(PALETTE_CONTEXT);
  } catch {
    return { status: "hidden" } as const;
  }
};

/** The parameters of a resolved intent, shown after its label: "Edit a chart… · Sales". */
const describeArgs = (action: CommandAction, args: CommandArgs): string[] =>
  (action.args ?? []).flatMap((spec) => {
    const value = args[spec.name];
    if (value === undefined || value === "") return [];
    if (spec.kind === "entity") return [spec.options().find((option) => option.id === String(value))?.label ?? String(value)];
    return [String(value)];
  });

/** The right-aligned meta of a story row: "Story · updated 3 days ago". */
const storyMeta = (story: PaletteStory): string =>
  story.updatedAt ? `${COMMAND_PALETTE_COPY.storiesHint} · ${COMMAND_PALETTE_COPY.storiesUpdated} ${formatRelativeTime(story.updatedAt)}` : COMMAND_PALETTE_COPY.storiesHint;

/** Everything the palette and the shortcuts sheet need, read from the command registry. */
export function useCommandPaletteHostState() {
  const registry = useCommandRegistry();
  const freeText = useCommandFreeText().handler;
  const { paletteOpen, shortcutsOpen, setPaletteOpen, setShortcutsOpen } = registry.ui;
  const [query, setQuery] = useState("");
  const [step, setStep] = useState<ArgStep | null>(null);
  const [resolution, setResolution] = useState<CommandResolution>({ kind: "none" });
  const requestRef = useRef(0);
  const sources = usePaletteSearchSources(paletteOpen);

  // Voice (Shift+Mod+V) opens the command list, which carries the mic. Only where the voice provider is on.
  useEffect(() => {
    if (!getSpeechProvider().isSupported()) return undefined;
    const mac = /mac|iphone|ipad/i.test(navigator.platform);
    const onKey = (event: KeyboardEvent) => {
      if (!chordMatches(event, VOICE_CHORD, mac)) return;
      event.preventDefault();
      setPaletteOpen(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setPaletteOpen]);

  // Each opening starts from a clean search.
  useEffect(() => {
    if (!paletteOpen) return;
    setQuery("");
    setStep(null);
    setResolution({ kind: "none" });
  }, [paletteOpen]);

  // Ask the resolvers what the typed text means; only the newest answer counts.
  useEffect(() => {
    const text = query.trim();
    const request = ++requestRef.current;
    if (!paletteOpen || step || !text) {
      setResolution({ kind: "none" });
      return;
    }
    registry.resolve(text, PALETTE_CONTEXT).then(
      (next) => {
        if (request === requestRef.current) setResolution(next);
      },
      () => {
        if (request === requestRef.current) setResolution({ kind: "none" });
      },
    );
    // `registry` identity is stable per provider; the typed text drives this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, step, paletteOpen]);

  const close = useCallback(() => setPaletteOpen(false), [setPaletteOpen]);

  const run = useCallback(
    async (id: string, args: CommandArgs) => {
      // Close first, so a confirmation shows over the page and focus returns to the opener.
      setPaletteOpen(false);
      const result = await registry.run(id, args, PALETTE_CONTEXT);
      const kind = RESULT_KIND[result.status];
      if (kind && result.message) notify({ kind, title: result.message });
    },
    [registry, setPaletteOpen],
  );

  const visibleActions = paletteOpen
    ? registry
        .list()
        .map((action) => ({ action, availability: availabilityOf(action) }))
        .filter(({ action, availability }) => availability.status !== "hidden" && action.id !== OPEN_PALETTE_ACTION_ID)
    : [];

  const toItem = ({ action, availability }: (typeof visibleActions)[number]): CommandListItem => ({
    id: `${ACTION_PREFIX}${action.id}`,
    label: action.label,
    hint: action.hint,
    shortcut: action.shortcut?.chords,
    shortcutSequence: action.shortcut?.sequence,
    // An action's own icon component, when it has one; otherwise Story's letter glyph.
    icon: action.icon
      ? createElement(action.icon, { size: 14, "aria-hidden": true })
      : action.group === "assistant"
        ? createElement("img", { src: MAGICBI_LOGO_SRC, alt: "MagicBI", width: 14, height: 14 })
        : glyphFor(action),
    soon: availability.status === "soon",
    disabledReason: availability.status === "blocked" ? availability.reason : undefined,
  });

  /** Stories of the workspace and, inside a story, its fields: fuzzy-matched on what is typed. */
  // PENDING: PENDING-SEARCH-STORIES, PENDING-SEARCH-FIELDS (LOCAL: live, no marker)
  const searchGroups = (): CommandListGroup[] => {
    const text = query.trim();
    if (!text) return [];
    const stories = rankByFuzzy(sources.stories, text, (story) => [story.title]).slice(0, GROUP_LIMIT);
    const fields = rankByFuzzy(sources.fields, text, (field) => [field.name]).slice(0, GROUP_LIMIT);
    const groups: CommandListGroup[] = [];
    if (stories.length) {
      groups.push({
        id: "stories",
        label: COMMAND_PALETTE_COPY.storiesGroup,
        items: stories.map((story) => ({ id: `${STORY_PREFIX}${story.id}`, label: story.title, hint: storyMeta(story), icon: "S", iconTone: "brand" as const })),
      });
    }
    if (fields.length) {
      groups.push({
        id: "fields",
        label: COMMAND_PALETTE_COPY.fieldsGroup,
        items: fields.map((field) => ({ id: `${FIELD_PREFIX}${sources.fields.indexOf(field)}`, label: field.name, hint: COMMAND_PALETTE_COPY.fieldsHint, icon: "#", iconTone: "info" as const })),
      });
    }
    return groups;
  };

  const buildGroups = (): CommandListGroup[] => {
    if (step) {
      const options = rankByFuzzy(step.spec.options(), query, (option) => [option.label]);
      return [{ id: "options", items: options.map((option) => ({ id: option.id, label: option.label })) }];
    }
    const lead: CommandListGroup[] = [];
    if (resolution.kind === "intent") {
      const action = registry.get(resolution.intent.actionId);
      if (action && availabilityOf(action).status === "ready") {
        const params = describeArgs(action, resolution.intent.args).join(" · ");
        lead.push({ id: "intent", items: [{ id: INTENT_ID, label: action.label, detail: params || undefined }] });
      }
    } else if (resolution.kind === "ambiguous") {
      lead.push({ id: "ambiguous", items: [{ id: AMBIGUOUS_ID, label: resolution.message, disabled: true }] });
    }
    if (query.trim()) {
      const ranked = rankByFuzzy(visibleActions, query, ({ action }) => [action.label, ...(action.keywords ?? [])]);
      // Free text goes last: a handler exists, and the words are not a step's search.
      const tail: CommandListGroup[] = freeText
        ? [{ id: "freeText", label: COMMAND_PALETTE_COPY.freeTextGroup, items: [{ id: FREE_TEXT_ID, label: COMMAND_PALETTE_COPY.freeTextRow, detail: `“${query.trim()}”`, shortcut: ASK_CHORD, icon: "✦", iconTone: "brand" }] }]
        : [];
      return [...lead, { id: "results", label: commandGroupLabel("story"), items: ranked.map(toItem) }, ...tail, ...searchGroups()];
    }
    const grouped = listCommandGroups(visibleActions.map(({ action }) => action)).map(({ id, label }): CommandListGroup => {
      const items = visibleActions.filter(({ action }) => action.group === id).map(toItem);
      // The "Coming soon" group, while all its rows are "Soon", is a strip of pills, not a list.
      const strip = STRIP_GROUP_IDS.includes(id) && items.length > 0 && items.every((item) => item.soon);
      return strip ? { id, label, items, variant: "chips" } : { id, label, items };
    });
    const ordered = grouped;
    return [...lead, ...ordered];
  };
  // Read fresh on every render, so availability and arg options are always current.
  const groups = buildGroups();

  /** Moves to the next missing required argument, or runs. */
  const advance = useCallback(
    (action: CommandAction, args: CommandArgs) => {
      const next = requiredEntitySpecs(action).find((spec) => args[spec.name] === undefined);
      if (next) {
        setStep({ action, args, spec: next });
        setQuery("");
        return;
      }
      void run(action.id, args);
    },
    [run],
  );

  /** Runs the page's add-filter action for the field; with none registered, says where to add it. */
  const filterOnField = useCallback(
    async (field: StoryFieldApi) => {
      const action = registry.get(ADD_FILTER_ACTION);
      if (action && availabilityOf(action).status === "ready") {
        await run(action.id, { field: field.name });
        return;
      }
      setPaletteOpen(false);
      notify({ kind: "info", title: `${COMMAND_PALETTE_COPY.filterHint} ${field.name}` });
    },
    [registry, run, setPaletteOpen],
  );

  const onSelect = useCallback(
    (id: string) => {
      if (step) {
        advance(step.action, { ...step.args, [step.spec.name]: id });
        return;
      }
      if (id === FREE_TEXT_ID) {
        const text = query.trim();
        setPaletteOpen(false);
        if (freeText && text) {
          Promise.resolve(freeText(text)).catch((error: unknown) => notify({ kind: "error", title: getApiErrorMessage(error, COMMAND_PALETTE_COPY.freeTextFailed) }));
        }
        return;
      }
      if (id === INTENT_ID && resolution.kind === "intent") {
        void run(resolution.intent.actionId, resolution.intent.args);
        return;
      }
      if (id.startsWith(STORY_PREFIX)) {
        const story = sources.stories.find((candidate) => candidate.id === id.slice(STORY_PREFIX.length));
        setPaletteOpen(false);
        if (story) sources.openStory(story);
        return;
      }
      if (id.startsWith(FIELD_PREFIX)) {
        const field = sources.fields[Number(id.slice(FIELD_PREFIX.length))];
        if (field) void filterOnField(field);
        return;
      }
      if (!id.startsWith(ACTION_PREFIX)) return;
      const action = registry.get(id.slice(ACTION_PREFIX.length));
      if (action) advance(action, {});
    },
    [advance, filterOnField, freeText, query, registry, resolution, run, setPaletteOpen, sources, step],
  );

  const onBack = useCallback(() => {
    setStep(null);
    setQuery("");
  }, []);

  const shortcutGroups = useMemo((): ShortcutGroup[] => {
    if (!shortcutsOpen) return [];
    const actions = registry.list().filter((action) => availabilityOf(action).status !== "hidden" && (action.shortcut?.chords.length ?? 0) > 0);
    const byGroup = listCommandGroups(actions).map(({ id, label }) => ({
      id,
      label,
      rows: actions.filter((action) => action.group === id).map((action) => ({ id: action.id, label: action.label, chords: action.shortcut?.chords ?? [], merged: action.shortcut?.sequence })),
    }));
    const palette: ShortcutGroup = {
      id: "palette",
      label: COMMAND_PALETTE_COPY.paletteGroup,
      rows: [
        { id: "move", label: COMMAND_PALETTE_COPY.moveThroughResults, chords: [{ key: "ArrowUp" }, { key: "ArrowDown" }] },
        { id: "run", label: COMMAND_PALETTE_COPY.run, chords: [{ key: "Enter" }] },
        { id: "close", label: COMMAND_PALETTE_COPY.closeHint, chords: [{ key: "Escape" }] },
      ],
    };
    const grid: ShortcutGroup = {
      id: "grid",
      label: COMMAND_PALETTE_COPY.gridGroup,
      rows: [...gridKeyboardShortcuts.map((row) => ({ id: row.id, label: row.action, chords: row.chords }))],
    };
    return [EVERYWHERE_SHORTCUTS, ...byGroup, grid, palette].map((group) => ({
      ...group,
      rows: mergeShortcutPairs(group.rows, SHORTCUT_PAIRS),
    }));
  }, [shortcutsOpen, registry]);

  return {
    paletteOpen,
    shortcutsOpen,
    query,
    setQuery,
    groups,
    crumb: step ? step.action.label : null,
    close,
    onSelect,
    onBack,
    closeShortcuts: () => setShortcutsOpen(false),
    shortcutGroups,
  };
}
